/* eslint-disable */
import type { LocalizableString } from "../cash/localization/localization.pb";
import type { Animation } from "../cash/ui/Animation.pb";
import type { Color } from "../cash/ui/Color.pb";
import type { Icon } from "../cash/ui/Icon.pb";
import type { Image } from "../cash/ui/Image.pb";
import type { Typography } from "../cash/ui/Typography.pb";
import type { BetweenSection } from "../cash/ui/arcade/elements/BetweenSection.pb";
import type { CellDefault } from "../cash/ui/arcade/elements/CellDefault.pb";
import type { HeroHeader } from "../cash/ui/arcade/elements/HeroHeader.pb";
import type { HeroNumericsHeader } from "../cash/ui/arcade/elements/HeroNumericsHeader.pb";
import type { ListOrdered } from "../cash/ui/arcade/elements/ListOrdered.pb";
import type { ListUnorderedSize } from "../cash/ui/arcade/elements/ListUnordered.pb";
import type { WithinSection } from "../cash/ui/arcade/elements/WithinSection.pb";
import type { Country } from "../common/countries.pb";
import type { Money } from "../common/currency.pb";
import type { CurrencyCode } from "../common/currency_codes.pb";
import type { GlobalAddress } from "../common/location.pb";
import type { BlockerAction } from "./blocker_action.pb";
import type { CardCustomizationData } from "./cards.pb";
import type { Avatar } from "./ui.pb";
import type { TimelineCollapseRanges } from "./ui/timeline.pb";
import type { UiAvatar } from "./ui/ui_avatar.pb";

/**
 * Describes a screen of stacked vertical content elements, potentially with one or two additional
 * fixed navigation buttons at the top or bottom.
 *
 * # Layout
 *
 * ## Spacing
 *
 * Elements are displayed in a vertical stack, in the order specified by the `elements` field.
 * The spacing between adjacent elements is hardcoded into the clients, but the server can specify
 * `SpacerElement`s in order to tailor the vertical alignment of content on the screen. A
 * `SpacerElement` is flexible in height, and consumes all available space. If multiple
 * `SpacerElement`s are specified then they each consume some portion of the remaining vertical
 * space, with the division specified by the `proportion` field.
 *
 * For example, to create vertically centered content the server could specify:
 *
 * ```
 * [SpacerElement(proportion=1), TextElement(size=MEDIUM), SpacerElement(proportion=1)]
 * ```
 *
 * To create a screen with top-aligned content and a footer:
 *
 * ```
 * [TextElement(size=MEDIUM), SpacerElement(proportional=1), TextElement(size=SMALL)]
 * ```
 *
 * ## Edge Cases
 *
 * Certain edge cases may come up for clients and should be handled as follows:
 *
 * If the client calculates that more content exists than can fit on the screen, then the screen
 * should scroll to expose the additional content. In this case any `SpacerElement`s should be
 * removed client-side and the standard hardcoded client spacing should be used.
 *
 * If no `SpacerElement` is included from the server, then an implicit `SpacerElement` should be
 * assumed at both the top and bottom of the content, thus making the content be centered.
 */
export interface FormBlocker {
  /** The content elements to display, in an ordered vertical stack. */
  elements?: FormBlockerElement[];
  /**
   * The primary action for navigation, displayed as a button at either the bottom or top of
   * the screen.
   *
   * If both a primary and secondary action are specified then the primary action button is
   * displayed on the right. If only one is specified then the action button is centered.
   */
  primary_action?: BlockerAction;
  primary_action_icon?: FormBlockerIcon;
  /**
   * The secondary action for navigation, displayed as a button at either the bottom or top of
   * the screen.
   *
   * If both a primary and secondary action are specified then the secondary action button is
   * displayed on the left. If only one is specified then the action button is centered.
   */
  secondary_action?: BlockerAction;
  /**
   * An optional disclaimer text that is shown above the primary and secondary actions. This field
   * can support markdown.
   */
  disclaimer_text?: string;
  /**
   * Additional FormBlockers which can be presented from this FormBlocker using ShowOverlayScreenAction
   * subforms do not currently support submission actions
   */
  subforms?: { [key: string]: FormBlocker };
  on_display_effect?: FormBlockerOnDisplayEffect | undefined;
  remote_on_display_effect?:
    | FormBlockerRemoteOnDisplayEffect
    | undefined;
  /**
   * True if the customer must scroll to the bottom to proceed. This will have no effect if the
   * content fits on the screen and is not scrollable.
   */
  requires_full_scroll?: boolean;
  /**
   * The semantic type of this button, if there is one. See [FormBlocker.Element.ButtonElement.Type]
   * docs for details.
   *
   * Optional; treated as NORMAL if null. Supported on 4.47+.
   */
  primary_action_type?: FormBlockerElementButtonElementType;
  /**
   * Style to be applied to the `secondary_action` field. If absent, `SECONDARY` will be
   * used.
   */
  secondary_action_style?: FormBlockerElementButtonElementStyle;
  /**
   * Style to be applied to the `primary_action` field. If absent, `PRIMARY` will be
   * used.
   * Available in client versions >= 5.22.
   */
  primary_action_style?: FormBlockerElementButtonElementStyle;
  /** The help action for displaying a help icon "?" button on the top of the screen. */
  help_action?: BlockerAction;
  /**
   * Information to the client about automatic field editing
   * This only takes effect if there are input fields on the page
   */
  initial_editing_mode?: FormBlockerInitialEditingMode;
}

export enum FormBlockerInitialEditingMode {
  /** DEFAULT - Defer to the client as to whether editing should begin */
  DEFAULT = "DEFAULT",
  /** NEVER - Wait for the user to select a field before editing */
  NEVER = "NEVER",
  /** ALWAYS - Always start editing when the form shows */
  ALWAYS = "ALWAYS",
}

export enum FormBlockerOnDisplayEffect {
  NONE = "NONE",
  CONFETTI = "CONFETTI",
}

/**
 * Represents a single vertical content element in the form.
 * Only one of the nested element fields will ever be set.
 */
export interface FormBlockerElement {
  /** An identifier to uniquely identify this element within the given form. */
  id?: string;
  /** Display elements. */
  copyable_element_group?: FormBlockerElementCopyableElementGroup | undefined;
  card_element?: FormBlockerElementCardElement | undefined;
  gift_card_element?: FormBlockerElementGiftCardElement | undefined;
  hero_element?: FormBlockerElementHeroElement | undefined;
  avatar_element?: FormBlockerElementAvatarElement | undefined;
  customized_card_element?: FormBlockerElementCustomizedCardElement | undefined;
  date_input_element?: FormBlockerElementDateInputElement | undefined;
  local_image_element?: FormBlockerElementLocalImageElement | undefined;
  money_element?: FormBlockerElementMoneyElement | undefined;
  text_element?: FormBlockerElementTextElement | undefined;
  remote_image_element?: FormBlockerElementRemoteImageElement | undefined;
  timeline_element?: FormBlockerElementTimelineElement | undefined;
  legal_text_element?: FormBlockerElementLegalTextElement | undefined;
  call_to_action_element?: FormBlockerElementCallToActionElement | undefined;
  captioned_tile_element?: FormBlockerElementCaptionedTileElement | undefined;
  notice_element?: FormBlockerElementNoticeElement | undefined;
  family_upsell_element?: FormBlockerElementFamilyUpsellElement | undefined;
  merchant_transaction_element?: FormBlockerElementMerchantTransactionElement | undefined;
  payment_plan_summary_element?: FormBlockerElementPaymentPlanSummaryElement | undefined;
  payment_plan_schedule_element?: FormBlockerElementPaymentPlanScheduleElement | undefined;
  upsell_element?: FormBlockerElementUpsellElement | undefined;
  checkbox_element?: FormBlockerElementCheckBoxElement | undefined;
  section_header_element?: FormBlockerElementSectionHeaderElement | undefined;
  hero_header_element?: FormBlockerElementHeroHeaderElement | undefined;
  hero_numerics_header_element?: FormBlockerElementHeroNumericsHeaderElement | undefined;
  editorial_content_element?:
    | FormBlockerEditorialContentElement
    | undefined;
  /** Input elements. */
  address_element?: FormBlockerElementAddressElement | undefined;
  button_element?: FormBlockerElementButtonElement | undefined;
  cashtag_element?: FormBlockerElementCashtagElement | undefined;
  option_picker_element?: FormBlockerElementOptionPickerElement | undefined;
  text_input_element?: FormBlockerElementTextInputElement | undefined;
  multiline_text_input_element?: FormBlockerElementMultilineTextInputElement | undefined;
  money_input_element?: FormBlockerElementMoneyInputElement | undefined;
  emoji_picker_element?: FormBlockerElementEmojiPickerElement | undefined;
  merchant_transaction_picker_element?: FormBlockerElementMerchantTransactionOptionPicker | undefined;
  location_element?: FormBlockerElementLocationElement | undefined;
  selectable_input_element?: FormBlockerElementSelectableInputElement | undefined;
  profile_preview_element?: FormBlockerElementProfilePreviewElement | undefined;
  verification_code_element?:
    | FormBlockerElementVerificationCodeElement
    | undefined;
  /** Layout elements. */
  divider_element?: FormBlockerElementDividerElement | undefined;
  spacer_element?: FormBlockerElementSpacerElement | undefined;
  fixed_spacer_element?:
    | FormBlockerElementFixedSpacerElement
    | undefined;
  /** List view elements. */
  detail_row_element?: FormBlockerElementDetailRowElement | undefined;
  selectable_row_element?: FormBlockerElementSelectableRowElement | undefined;
  list_unordered_element?: FormBlockerElementListUnorderedElement | undefined;
  cell_default_element?: FormBlockerElementCellDefaultElement | undefined;
  list_ordered_element?: FormBlockerElementListOrderedElement | undefined;
}

/** A field to collect an address */
export interface FormBlockerElementAddressElement {
  /** The initial value with which to populate the address fields. Optional. */
  prefill_address?: GlobalAddress;
  /** The country where the expected address is located. */
  country_code?: Country;
  /**
   * If true, then show autocomplete results as the customer types.
   * If false, then show discrete fields for each aspect of the address, such as
   * state, ZIP, etc.
   */
  autocomplete?: boolean;
  /**
   * If true, then enable autofill.
   * If false, disable autofill.
   */
  autofill?: boolean;
}

/**
 * An element to display one or more avatars for entities (Customers, Merchants, etc) in the Cash system.
 *
 * An avatar can be displayed either as a couple of characters (`monogram_text`) on a colored background
 * (`background_color`) or as a photo downloaded from the server (`photo_url`), or, if neither `photo_url`
 * nor `monogram_text` is specified, a generic customer icon will be shown on a colored background.
 *
 * If a photo is specified then the monogram/accent color version will be shown as a placeholder while the
 * photo is downloaded and loaded.
 */
export interface FormBlockerElementAvatarElement {
  /** If more than 1 avatar is provided, they will be diagonally stacked. */
  avatars?: FormBlockerElementAvatarElementAvatar[];
  /** An optional badge to display in the lower right corner of the image. Available in client versions >= 3.46. */
  badge?: FormBlockerElementAvatarElementBadge;
  /** The background color to show with the badge, if provided. Available in client versions >= 3.46. */
  badge_background_color?: Color;
  /**
   * If set, override size of all avatars in the element, otherwise avatars follow client sizing logic.
   * Available in client versions >= 5.7
   */
  avatars_size?: FormBlockerElementAvatarElementAvatar_Size;
  /**
   * If both `avatars` and `ui_avatars` are populated, `ui_avatars` takes priority.
   * Supported in mobile clients 5.13+
   */
  ui_avatars?: UiAvatar[];
}

/**
 * A glyph that may be shown stacked in the lower right corner of the element.
 * Available in client versions >= 3.46.
 */
export enum FormBlockerElementAvatarElementBadge {
  /** CHECKMARK - Show a checkmark icon */
  CHECKMARK = "CHECKMARK",
  /** PAYMENT_PLAN - Shows the payment plan split icon */
  PAYMENT_PLAN = "PAYMENT_PLAN",
  /** RECURRING_SINGLE_ARROW - (Unsupported) Show the recurring icon with a single arrow */
  RECURRING_SINGLE_ARROW = "RECURRING_SINGLE_ARROW",
  /** LINK - Show a link/chain icon. Available for Android version >= 4.92. For iOS version >= 4.90. */
  LINK = "LINK",
}

/** Avatar sizes as declared in https://www.figma.com/design/xK1aLwpxdjeQAbkUjbwcfP/01-Arcade---Assets?node-id=2611-1053&m=dev */
export enum FormBlockerElementAvatarElementAvatar_Size {
  /** SMALL - 32px, text size: 16px, icon size: 16px */
  SMALL = "SMALL",
  /** MEDIUM - 48px, text size: 24px, icon size: 24px */
  MEDIUM = "MEDIUM",
  /** LARGE - 64px, text size: 32px, icon size: 32px */
  LARGE = "LARGE",
  /** EXTRA_LARGE - 96px, text size 48px, icon size: 48px */
  EXTRA_LARGE = "EXTRA_LARGE",
}

export interface FormBlockerElementAvatarElementAvatar {
  /**
   * The accent color to use as the background of the avatar if no photo is present.
   * 6 digit hex with leading # sign (ex: "#BBBFB3").
   * If not specified, the client will fall back to using the Gray 85 color, #D9D9D9.
   * Optional.
   *
   * Deprecated. Use `background_color` instead.
   *
   * @deprecated
   */
  accent_color?: string;
  /**
   * The accent color to use as the background of the avatar. Used with `monogram_text`. NOT used with `image`
   * UNLESS `is_template_avatar` is true. If not specified, the client will fall back to using the Gray 85 color,
   * #D9D9D9.
   * Optional.
   */
  background_color?: Color;
  /**
   * The monogram text to display in the middle of the avatar if no photo is present.
   * Currently only one character is allowed.
   * Optional.
   */
  monogram_text?: string;
  /**
   * The URL for the photo to display in the avatar, in place of a background color and monogram.
   * Optional.
   *
   * Deprecated. Use `image` instead.
   *
   * @deprecated
   */
  photo_url?: string;
  /**
   * The accessibility value to apply on the client.
   * Most likely the display name for the entity that the avatar represents.
   * Optional.
   */
  accessibility_value?: string;
  localizable_accessibility_value?: LocalizableString;
  /**
   * Light- and dark-themed URLs for the photo to display in the avatar, in place of a background color and monogram.
   * Optional.
   */
  image?: Image;
  /**
   * True if `image` is a template image and should be colorized/tinted. `background_color` will also be shown
   * if this is true. `background_color` will NOT be shown with `image` if this is false or null.
   * Available in client versions >= 3.53.
   */
  is_template_avatar?: boolean;
  /** The remote badge image, available only to clients on version 3.56 and above. */
  badge_image?: Image;
  badge_shape?: FormBlockerElementAvatarElementAvatarBadgeShape;
  /**
   * Counter value used to display stacked on the avatar, prexifed with "+".
   * Max vaue 99, rendered in standard text on background color
   * Display priority: image > monogram_text > counter_text
   */
  counter_value?: number;
}

export enum FormBlockerElementAvatarElementAvatarBadgeShape {
  /** CIRCULAR - Circular shape, which is the default. */
  CIRCULAR = "CIRCULAR",
  /** CARD - A card shape, used for gift cards. */
  CARD = "CARD",
}

/** A button which performs an action. */
export interface FormBlockerElementButtonElement {
  action?: BlockerAction;
  /** The style with which to display the button. */
  style?: FormBlockerElementButtonElementStyle;
  /**
   * The semantic type of this button, if there is one. See [Type] docs for details.
   *
   * Optional; treated as NORMAL if null. Supported on 4.46+.
   */
  type?: FormBlockerElementButtonElementType;
  /**
   * Size of the button.
   * Optional; treated as DEFAULT if null.
   */
  size?: FormBlockerElementButtonElementSize;
}

export enum FormBlockerElementButtonElementStyle {
  /** PRIMARY - Appropriate for the main actions the customer may select. */
  PRIMARY = "PRIMARY",
  /**
   * SECONDARY - Appropriate for auxiliary actions that a customer can take outside the
   * scope of the blocker's main purpose.
   */
  SECONDARY = "SECONDARY",
  /**
   * TERTIARY - Appropriate for yet less important actions that a customer can take outside the
   * scope of the blocker's main purpose.
   */
  TERTIARY = "TERTIARY",
}

/** The semantic type of the button. */
export enum FormBlockerElementButtonElementType {
  /** NORMAL - The button is given no special treatment. This is the default. */
  NORMAL = "NORMAL",
  /** DESTRUCTIVE - The button is for a destructive action, and may be rendered with a red accent. */
  DESTRUCTIVE = "DESTRUCTIVE",
}

/**
 * The size of the button. DEFAULT and CTA will expand to fill the horizontal space.
 * COMPACT will shrink to fit and be left aligned.
 */
export enum FormBlockerElementButtonElementSize {
  /** DEFAULT - Sizing matching Arcade Default buttons */
  DEFAULT = "DEFAULT",
  /**
   * CTA - Sizing matching Arcade CTA buttons
   * CTA buttons will also be used for primary_action and secondary_action
   */
  CTA = "CTA",
  /**
   * COMPACT - Sizing matching Arcade Compact buttons
   * Available in client versions >= 5.22.
   */
  COMPACT = "COMPACT",
}

/**
 * An text field which allows the customer to select their Cashtag.
 * This element should provide real-time feedback to the customer as they type
 * which indicates whether or not the currently entered Cashtag is available.
 * This availability information can be fetched from `get-cashtag-status`.
 */
export interface FormBlockerElementCashtagElement {
  /**
   * The symbol to always show as a prefix to the entered Cashtag. For the US this is "$".
   * Although this should almost always be a single character, clients should support an
   * empty prefix or multiple characters.
   */
  prefix_symbol?: string;
  /**
   * If non-null, this should be used as light grey hint text for the entry
   * field, which disappears once the user starts typing.
   */
  hint_text?: string;
  /**
   * A suggested Cashtag for the customer. May be null if no good suggestion is available.
   * This value should not include the prefix symbol, such as "$". If non-null, this value
   * should be used to pre-populate the entry field.
   */
  prefill_text?: string;
}

/** An element for collecting the day, month, and year of a date. */
export interface FormBlockerElementDateInputElement {
  /**
   * The earliest possible date that can be entered in the field.
   * This value is inclusive, and the minimum date is itself considered a valid date.
   *
   * The minimum date is specified as an ISO 8601 string and therefore includes a
   * time and time zone, which should be considered when specifying the value. If
   * any part of a given day is on or after the minimum date as then it is considered valid.
   * The implementation for this be roughly equivalent to:
   *   - Create a date object with the entered date at 23:59 in the local time zone
   *   - Ensure that the created date is after the minimum date.
   *
   * If a value is entered that falls before the minimum date, or a partial date is entered
   * that cannot fall on or after the minimum date, then the specific field being edited
   * (such as the year field) should shake to indicate that the attempted entry is invalid.
   *
   * This value is optional. If not specified then Jan 1, 1900 in the local time zone
   * will be assumed to be the minimum date.
   */
  minimum_date?: string;
  /**
   * The latest possible date that can be entered in the field.
   * This value is inclusive, and the maximum date is itself considered a valid date.
   *
   * The maximum date is specified as an ISO 8601 string and therefore includes a
   * time and time zone, which should be considered when specifying the value. If
   * any part of a given day is on or before the maximum date then it is considered valid.
   * The implementation for this be roughly equivalent to:
   *   - Create a date object with the entered date at 00:00 in the local time zone
   *   - Ensure that the created date is before the maximum date.
   *
   * If a value is entered that falls after the maximum date, or a partial date is entered
   * that cannot fall on or before the maximum date, then the specific field being edited
   * (such as the year field) should shake to indicate that the attempted entry is invalid.
   *
   * This value is optional. If not specified then Dec 31, 2099 in the local time zone
   * will be assumed to be the maximum date.
   */
  maximum_date?: string;
  /** The accessibility label for this element. */
  accessibility_text?: string;
  /**
   * If true, the entry fields should allow empty values.
   * If not set, default to false for backwards compatibility.
   * Available in client versions >= 3.54.
   */
  is_optional?: boolean;
  /**
   * The default value to use for the input field. This value is optional, and if omitted
   * no default should be set. This is a date only string with no time component formatted
   * as an ISO_DATE("yyyy-mm-dd")
   */
  default_value?: string;
  /**
   * The label to be displayed above the field when using the Arcade design system.
   * If label_text is null then no title will be displayed
   */
  label_text?: string;
  /**
   * If true, then enable autofill.
   * If false, disable autofill.
   */
  autofill?: boolean;
}

/**
 * A divider line between two other elements. This line is displayed in gray and spans
 * the full width of the screen, defaults to a HAIRLINE divider.
 * The styles provided do not support modern designs. See `FixedSpacerElement` for Arcade spacing.
 */
export interface FormBlockerElementDividerElement {
  /** The style that should be applied to the divider line by the client. */
  style?: FormBlockerElementDividerElementDividerStyle;
}

/** The style that should be applied to the divider line. */
export enum FormBlockerElementDividerElementDividerStyle {
  /** HAIRLINE - A hairline-thick divider line as narrow as the client can display. */
  HAIRLINE = "HAIRLINE",
  /** ELEMENT - A medium-sized divider, less prominent than a SECTION divider. */
  ELEMENT = "ELEMENT",
  /** SECTION - A larger divider, used to separate sections from each-other. */
  SECTION = "SECTION",
}

/**
 * A field to collect generic text input. This is a highly flexible field which allows for
 * realtime validation of user input and realtime formatting of the output while it is being
 * typed.
 *
 * Some of the sample inputs which can be collected and validated using this field include
 * Name, SSN, email, password, and confirmation code. Some input types, such as phone numbers
 * or credit cards require additional UI and complexity which warrant their own element types.
 *
 * To model an SSN input, you would specify something like:
 *   validation = Validation()
 *   validation.regex = "\d+"
 *   validation.template.template = "XXX-XXX-XXX"
 *   validation.template.template_placeholder_character = "X"
 *
 *   inputField = InputField()
 *   inputField.keyboardType = NUMBER_PAD
 *   inputField.hint_text = "SSN"
 *   inputField.validations.add(validation)
 *
 *   inputElement = TextInputElement()
 *   inputElement.inputFields.add(inputField)
 */
export interface FormBlockerElementTextInputElement {
  /**
   * The text input fields to display to the user. These should be displayed as
   * a vertical stack if more than one is present.
   */
  input_fields?: FormBlockerElementTextInputElementInputField[];
  /**
   * Optional. Supplementary left-aligned text displayed below input fields.
   * Rendered in SMALL font with a lock glyph to the left of the text.
   */
  security_text?: string;
}

/**
 * The type of keyboard to display for a given field.
 * If a client does not support a given keyboard type then it is responsible for falling
 * back to another type which provides a superset of the specified keyboard's functionality.
 *
 * For reference:
 *   Android keyboard types: https://developer.android.com/reference/android/text/InputType
 *   iOS keyboard types: https://developer.apple.com/documentation/uikit/uikeyboardtype
 */
export enum FormBlockerElementTextInputElementKeyboardType {
  /** DEFAULT - A standard full keyboard, such as the QWERTY keyboard for English countries. */
  DEFAULT = "DEFAULT",
  /** NUMBER_PAD - A numbers-only keyboard which includes only 0-9 and the backspace key. */
  NUMBER_PAD = "NUMBER_PAD",
  /** NUMBER_PAD_WITH_DECIMAL - A numbers keyboard that also includes a decimal (.) key. */
  NUMBER_PAD_WITH_DECIMAL = "NUMBER_PAD_WITH_DECIMAL",
  /** NUMBER_PAD_WITH_ABC - A numbers keyboard that also allows switching to a full keyboard. */
  NUMBER_PAD_WITH_ABC = "NUMBER_PAD_WITH_ABC",
  /**
   * EMAIL - A keyboard with a layout optimized for entry of email addresses. If a client does not
   * support such a keyboard then the client should fall back to the `DEFAULT` keyboard.
   */
  EMAIL = "EMAIL",
}

/** The display security for the text in the field. */
export enum FormBlockerElementTextInputElementSecurity {
  /** CLEAR_TEXT - Text is fully displayed. */
  CLEAR_TEXT = "CLEAR_TEXT",
  /** SECURE - Text is obfuscated as it's typed, like a standard password field. */
  SECURE = "SECURE",
}

/**
 * Defines what characters can be entered into the field and does realtime formatting
 * to insert characters that help with readability.
 *
 * For example, given a regex of "\d+" and a template of "XXX-XXX", the user's input
 * of "123ab456" would be displayed as "123-456". The letters would be rejected and
 * ignored because they don't pass the regex, and the dash would be inserted because
 * of the template.
 */
export interface FormBlockerElementTextInputElementValidation {
  /**
   * A regular expression defining what a valid user-entered value looks like.
   * This expression should match a full and complete value that is ready to
   * be submitted to the server.
   * *
   * If text entered by the user fully, or *partially* (as a prefix) matches
   * this regex, then the accompanying template will be applied to the text as
   * the user types.
   *
   * Prefix matching in this case means that the current input
   * still has the potential to match the regex by appending additional characters.
   * For example, with the regex "\d{3}", the inputs "1", "12", and "123" are all
   * potential matches, but the inputs "a", "1a", and "1234" are not.
   *
   * The regex defines valid characters input by the user, and if an accompanying
   * template is being used then the regex should not include any format characters
   * inserted by the template. For example, the regex for a valid US phone
   * number would be "\d{10}" rather than something like "(\d{3}) \d{3}-\d{4}".
   * Clients should strip the template's formatting characters before attempting to
   * apply the regex.
   *
   * A validation's template implicitly specifies the required length of a user's
   * input by specifying the number of token characters which must be filled. This
   * has the potential to conflict with the length specified by the regex (which
   * would be a bug). To avoid this, it's recommended that if a template is being
   * used then no length limitation is specified in the regex.
   *
   * Some common regex values include:
   *   "\d+"     - User can only enter numbers. Combine with a template to limit
   *               length.
   *
   *   "\d{3}"   - User can only enter three numbers. Useful if there is no template
   *               to limit length.
   *
   *   ".{3,20}" - User must enter at least three characters and no more than 20.
   *               Any character is allowed.
   */
  regex?: string;
  /**
   * The template used to specify where formatting characters should be inserted.
   * If no formatting characters are needed then this field can be omitted.
   */
  template?: FormBlockerElementTextInputElementTemplate;
}

/**
 * A specification for how to format a given string, such as:
 *  "123456" (input) + "XXX-XXX" (template) -> "123-456"
 *
 * A Swift implementation for this logic is available here:
 * https://github.com/square/StringTemplate
 */
export interface FormBlockerElementTextInputElementTemplate {
  /**
   * A template string which will be applied to the user's text as it is
   * typed. For example, a template of "XXX-XXX-XXX" would automatically insert
   * a dash after the user has typed the third character, and again after
   * the sixth.
   */
  template?: string;
  /**
   * The character in the template string which should be replaced (one or more
   * times) in the template string with characters entered by the user.
   *
   * This must be a single character. In the example above, "X" is used.
   */
  template_placeholder_character?: string;
  /**
   * Characters that should be treated like user-entered characters even if
   * they may have been inserted as part of the template. The string value in
   * this case is treated as an unordered set of characters.
   *
   * This should only be needed in cases where the template may automatically
   * insert a character which the user can also enter and usually should not
   * be required.
   *
   * As an example, a UK phone number is prefixed by a zero, but the user may
   * also need to enter a zero later in the phone number, like "07 502332".
   * The template for this would look like "0X XXXXXX".
   *
   * Without special casing for "0" in this case, the template would be applied
   * to the input "7502332" like so:
   *  - Strip format characters ("0" and " ") -> "752332" (no zeros)
   *  - Apply template: "07 52332" (note missing second zero)
   *
   * By specifying the value for this field as "0" the template will preserve
   * the "0" in the input string and create the correct result.
   */
  user_insertable_format_characters?: string;
}

/**
 * A single field within the text input area. The text input element may
 * have multiple vertically-stacked input fields if needed.
 */
export interface FormBlockerElementTextInputElementInputField {
  /**
   * If non-null, this should be used as light grey hint text for the entry
   * field, which disappears once the user starts typing.
   */
  hint_text?: string;
  /** If non-null, this should be used to pre-populate the entry field. */
  prefill_text?: string;
  /**
   * The type of keyboard to display. If null then the `DEFAULT` keyboard
   * should be used.
   */
  keyboardType?: FormBlockerElementTextInputElementKeyboardType;
  /** The level of security used when displaying the text. */
  security?: FormBlockerElementTextInputElementSecurity;
  /**
   * The validation and formatting to use when text is being entered.
   *
   * Note that this is an ordered, repeated field. If multiple validation entries
   * exist then the one used will be the first one that includes a regex where the
   * current text matches, or could still potentially match after adding additional
   * characters.
   *
   * For example, to support either a five-digit or nine-digit postal code, these
   * two regex/template pairs could be used:
   *   "\d{5}", "XXXXX"
   *   "\d{9}", "XXXXX-XXXX"
   *
   * After the user types the sixth number the first regex no longer matches, nor
   * has the potential to match by typing more characters at the end of the input.
   * Given this, the formatting for the second would be used.
   */
  validations?: FormBlockerElementTextInputElementValidation[];
  /**
   * true indicates that the entry field supports multiple lines of text.
   * false or null indicate that only single line input is supported.
   *
   * Deprecated by MultilineTextInputElement.
   *
   * @deprecated
   */
  multiline?: boolean;
  /**
   * An optional accessory view to display for SECURE fields; ignored for non-SECURE fields.
   *
   * Deprecated by trailing_field_accessory, which is supported by mobile app version >= 4.89 and the web app.
   * Some services are still sending `secure_field_accessory` instead of `trailing_field_accessory`
   * so clients should account for both right now.
   *
   * @deprecated
   */
  secure_field_accessory?: FormBlockerElementTextInputElementInputFieldSecureFieldAccessory;
  /**
   * The label to be displayed above the field when using the Arcade design system.
   * If label_text is null then no title will be displayed
   * Design: https://www.figma.com/file/fu8vNWffYkGUMyznuQyzhc/02-Arcade---Mobile?node-id=10760-18950
   */
  label_text?: string;
  /**
   * When true the input field will use the disabled design and editing will not be allowed
   * Supported by Android 4.64+ and iOS 4.64+
   */
  disabled?: boolean;
  /**
   * An optional accessory to display on the right of a field.
   *
   * Supported by mobile app version >= 4.89 and the web app.
   */
  trailing_field_accessory?: FormBlockerElementTextInputElementInputFieldTrailingFieldAccessory;
  /**
   * The autofill type to be used for input view.
   * Set to DISABLED or null if no autofill is expected.
   */
  autofill_content_type?: FormBlockerElementTextInputElementInputFieldAutofillContentType;
  /**
   * Capitalization to be applied to entered text
   * Clients will default to sentence capitalization if no value is set
   * Supported by mobile app version >= 5.3.0
   */
  capitalization?: FormBlockerElementTextInputElementInputFieldCapitalizationType;
  /**
   * List of inputs which are considered invalid and will convert the field into the error state
   * If multiple invalid inputs match then the lowest matching index will be used.
   * Supported by mobile app version >= 5.5.0
   */
  invalid_inputs?: FormBlockerElementTextInputElementInputFieldInvalidInput[];
}

/**
 * An accessory view to display for SECURE fields. Optional.
 *
 * Deprecated by trailing_field_accessory, which is supported by mobile app version >= 4.89 and the web app.
 * Some services are still sending `secure_field_accessory` instead of `trailing_field_accessory`
 * so clients should account for both right now.
 *
 * @deprecated
 */
export enum FormBlockerElementTextInputElementInputFieldSecureFieldAccessory {
  /** NONE - No accessory. */
  NONE = "NONE",
  /**
   * LOCK - A lock glyph displayed to the right of the field.
   * Nothing happens if the glyph is tapped.
   */
  LOCK = "LOCK",
  /**
   * SHOW_HIDE - A show/hide glyph displayed to the right of the field,
   * which either obfuscates or displays the input string when tapped.
   */
  SHOW_HIDE = "SHOW_HIDE",
}

/** An accessory to display on the right of the field. Optional. */
export enum FormBlockerElementTextInputElementInputFieldTrailingFieldAccessory {
  /** ABSENT - No accessory. */
  ABSENT = "ABSENT",
  /**
   * SECURE_FIELD_LOCK - A lock glyph displayed to the right of the field.
   * Only displayed on fields with `Security == SECURE`.
   * Nothing happens if the glyph is tapped.
   */
  SECURE_FIELD_LOCK = "SECURE_FIELD_LOCK",
  /**
   * SECURE_FIELD_SHOW_HIDE - A show/hide glyph displayed to the right of the field,
   * which either obfuscates or displays the input string when tapped.
   * Only displayed on fields with `Security == SECURE`.
   */
  SECURE_FIELD_SHOW_HIDE = "SECURE_FIELD_SHOW_HIDE",
  /**
   * CLEAR_BUTTON - A button that can be used to clear the current input
   * Displayed regardless of the `Security` value.
   */
  CLEAR_BUTTON = "CLEAR_BUTTON",
}

/**
 * Defines autofill content type to be used for an input field.
 * If set to DISABLED or null, then content type is not set.
 * If set to any other value then mobile client will suggest autofill
 * data when input view takes focus.
 */
export enum FormBlockerElementTextInputElementInputFieldAutofillContentType {
  DISABLED = "DISABLED",
  FIRST_NAME = "FIRST_NAME",
  LAST_NAME = "LAST_NAME",
  FULL_ADDRESS = "FULL_ADDRESS",
  STREET_ADDRESS_LINE_1 = "STREET_ADDRESS_LINE_1",
  STREET_ADDRESS_LINE_2 = "STREET_ADDRESS_LINE_2",
  CITY = "CITY",
  STATE = "STATE",
  POSTAL_CODE = "POSTAL_CODE",
  BIRTH_DATE = "BIRTH_DATE",
  PHONE_NUMBER = "PHONE_NUMBER",
  EMAIL = "EMAIL",
}

/** Capitalization to be applied to entered text */
export enum FormBlockerElementTextInputElementInputFieldCapitalizationType {
  NO_CAPITALIZATION = "NO_CAPITALIZATION",
  WORDS = "WORDS",
  SENTENCES = "SENTENCES",
  ALL_CHARACTERS = "ALL_CHARACTERS",
}

/**
 * Represents an invalid input which will convert the input field to an error state.
 * This functions as the inverse of `Validation` which defines which characters are valid in the InputField.
 * `InvalidInput` does not prevent characters from being entered into the field, instead showing an error.
 * While any InvalidInput matches the current input, the InputField will use the Arcade error state.
 * The form cannot be submitted while the current text matches any `InvalidInput`
 * This can be used after server side validation of previous inputs by setting the regex to be an exact string match
 * of the value sent to the server along with the desired error.
 */
export interface FormBlockerElementTextInputElementInputFieldInvalidInput {
  /**
   * If the regex matches the current text in the input field then this error case will be triggered
   * If the regex is empty it will match all strings. Invalid regexes will cause a formBlocker parsing error
   */
  regex?: string;
  /**
   * The message to show below the input field in the help text area.
   * If the message is empty, then the field will adopt the error state without any help text
   */
  message?: string;
}

/**
 * A text field that supports multiline should start as a single line and
 * expand vertically to accommodate additional content as the user types.
 * If content is deleted it should contract back so that only as much vertical
 * space is used as is needed.
 */
export interface FormBlockerElementMultilineTextInputElement {
  /**
   * If non-null, this should be used as light grey hint text for the entry
   * field, which disappears once the user starts typing.
   */
  hint_text?: string;
  /** If non-null, this should be used to pre-populate the entry field. */
  prefill_text?: string;
  /**
   * If non-null, this is the maximum number of UTF-16 characters that this entry field should
   * allow. null means there is no limitation on the number of characters.
   */
  max_character_count?: number;
  /**
   * If true, the entry field should not allow entering newline characters. Any newline
   * characters in pasted text should be converted to spaces. It is an error to submit input
   * containing newlines when this field is set to true.
   */
  disallow_newlines?: boolean;
  /**
   * If true, the entry field should collapse sequences of whitespace characters into one space
   * character. Any whitespace in pasted text should be collapsed as well. It is an error to
   * submit input containing sequences of whitespace characters when this field is set to true.
   */
  collapse_whitespace?: boolean;
  /**
   * If true, the entry field should not allow non-empty text.
   * If not set, default to false for backwards compatibility.
   * Available in client versions >= 3.63.
   */
  is_required?: boolean;
  /**
   * The label to be displayed above the field when using the Arcade design system.
   * If label_text is null then no title will be displayed
   */
  label_text?: string;
  /**
   * If non-null, this is the minimum number of UTF-16 characters that this entry field requires.
   * When is_required is true this field can optionally contain a value. If there is a minimum
   * character count set, the app will display a prompt to the user indicating how many remaining
   * characters they must enter to be value. If this is null or 0 then the app will simply prompt
   * "This field is required".
   * If is_required is false then this field will be ignored.
   * Available in client versions >= 4.81.
   */
  min_character_count?: number;
}

/**
 * A field to collect money amount input. This is an input field which allows for
 * money input specifically with support for realtime validation and denomination settings.
 */
export interface FormBlockerElementMoneyInputElement {
  /** The currency the money amount should be in, which is used to display the currency sign. */
  currency_code?: CurrencyCode;
  /**
   * If non-null, this should be used as light grey hint text for the entry
   * field, which disappears once the user starts typing.
   */
  hint_text?: string;
  /** If non-null, this should be used to pre-populate the entry field. */
  prefill_amount?: Money;
  /** If non-null, this is the minimum amount (inclusive) used to validate against the entered amount */
  minimum_amount?: Money;
  /** If non-null, this is the maximum amount (inclusive) used to validate against the entered amount */
  maximum_amount?: Money;
  /** If true, the entry fields should allow empty values. */
  is_optional?: boolean;
  /**
   * If true, adds decorator outline to entry field. Defaults to false. Available in client version 4.32+.
   * This has no effect when Arcade is enabled.
   */
  show_decorator?: boolean;
  /**
   * The label to be displayed above the field when using the Arcade design system.
   * If label_text is null then no title will be displayed
   */
  label_text?: string;
  /**
   * TO BE DEPRECATED: Use CENTS denomination_option instead.
   * If true, only fractional values are allowed to be input into the entry field.
   * When the field is using the Arcade design system, the field will have a fixed prefix of
   * zero in the provided currency instead of just the currency symbol, e.g. a prefix of `$0.`
   * instead of just `$`.
   */
  fractional_values_only?: boolean;
  /**
   * TO BE DEPRECATED: Use DOLLARS denomination_option instead.
   * If true, the input field will only allow whole number inputs.
   * If not set, default to false for backwards compatibility.
   */
  disable_decimal?: boolean;
  /**
   * If the field contains a parseable amount, but it sits outside the range specified by `minimum_amount` and
   * `maximum_amount`, outline the field in an error color and show an error String describing the valid
   * range. eg ("You must enter an amount less than $1,0000,000.00").
   */
  highlight_min_max_violations?: boolean;
  /**
   * Optional denomination option, if not set default to DOLLARS_AND_CENTS.
   * - DOLLARS: The input field will only allow whole number inputs.
   * - CENTS: When using with Arcade design system, the field will have a fixed prefix of
   *   zero in the provided currency instead of just the currency symbol, e.g. a prefix of `$0.`
   *   instead of just `$`.
   * Supported on client versions 4.91+
   */
  denomination_option?: FormBlockerElementMoneyInputElementDenominationOption;
}

/**
 * Denomination display options supported by Money Formatter Multiplatform
 * https://github.com/squareup/cash-moneyformatter-multiplatform?tab=readme-ov-file#custom-formatting-style
 */
export enum FormBlockerElementMoneyInputElementDenominationOption {
  DOLLARS_AND_CENTS = "DOLLARS_AND_CENTS",
  DOLLARS = "DOLLARS",
  CENTS = "CENTS",
}

/**
 * A selectable text field with a background that requires further action to fill. If prefill
 * text is not available, then the hint text will be shown as secondary label. Prefill text can
 * be updated/replaced by tapping on the field to initiate a blocker action.
 *
 * Available in client versions >= 4.8.
 */
export interface FormBlockerElementSelectableInputElement {
  /** Prefill text shown if input fill data is available. Shown in label color. */
  prefill_text?: string;
  /** Hint text shown if prefill text is unavailable. Shown in secondary label color. */
  hint_text?: string;
  /** Action taken when anywhere in the row is tapped. Required to fill the element. */
  input_action?: FormBlockerElementSelectableInputElementAction;
  /** An optional title displayed above the input. Shown in secondary label color. */
  title_text?: string;
  /** An optional footer displayed below the input. Shown in tertiary label color. */
  footer_text?: string;
}

/** Action taken when anywhere in the row is tapped. */
export interface FormBlockerElementSelectableInputElementAction {
  action?: BlockerAction;
}

/** An element repesenting a Profile. */
export interface FormBlockerElementProfilePreviewElement {
  /** A required avatar for the Profile. */
  avatar?: UiAvatar;
  /** Optional caption rendered under the avatar. Only used with center alignment. */
  caption?: FormBlockerElementProfilePreviewElementCaption;
  /** A required title for the Profile, usually this is the Display Name. */
  title?: string;
  /** A required subtitle rendered under the title for the Profile, usually this is the cashtag. */
  subtitle?: string;
  /** Optional footer text. Only used with center alignment. */
  footer?: string;
  /** Optional Alignment, if not set default to CENTER */
  alignment?: FormBlockerElementProfilePreviewElementAlignment;
  titleBadge?: FormBlockerElementProfilePreviewElementBadge | undefined;
  pill?: FormBlockerElementProfilePreviewElementPill | undefined;
}

export enum FormBlockerElementProfilePreviewElementAlignment {
  CENTER = "CENTER",
  LEADING = "LEADING",
}

/** Optional caption rendered under the avatar. */
export interface FormBlockerElementProfilePreviewElementCaption {
  /** Optional icon rendered to the left of the caption text. */
  icon?: Image;
  /** Optional */
  text?: string;
}

export interface FormBlockerElementProfilePreviewElementBadge {
  icon?: Image;
  /** Optional color used to tint the badge. If not set default local color is used. */
  tint_color?: Color;
}

export interface FormBlockerElementProfilePreviewElementPill {
  text?: string;
  /** Optional color used to set text color. */
  text_color?: Color;
  /** Optional color used to set pill background color. */
  background_color?: Color;
}

/** An element used to allow users to pick an option out of a static list of options. */
export interface FormBlockerElementOptionPickerElement {
  /**
   * The options that the customer can select, where the value of the option is actually
   * displayed to the user.  This will be an ordered list, and the client should maintain that
   * order when presenting.
   */
  options?: FormBlockerElementOptionPickerElementOption[];
  /**
   * The index of the option to show as initially selected.
   * If this property is not set then no option should be selected by default. Clients should
   * be careful to differentiate between "unset" and reading the default value of zero.
   */
  selected_option?: number;
  /**
   * If true, customer can submit the form without selecting any option.
   * If not set, default to false for backwards compatibility.
   * Available in client versions >= 3.59.
   */
  is_optional?: boolean;
  /**
   * Deprecated and not implemented on the clients. The bordered option picker is the new arcade style.
   * See the form-blocker-arcade-option-picker/targeting feature flag for the arcade style
   * roll out. Style to apply to Options. If nil, Orientation defaults to LEFT
   * and SelectionStyle to DEFTAULT.
   */
  style?: FormBlockerElementOptionPickerElementStyle;
}

export interface FormBlockerElementOptionPickerElementOption {
  /**
   * A machine-read identifier to uniquely identify this option within the list of options.
   * This will be the same for everyone choosing this option regardless of the value it was
   * actually displayed with.
   */
  id?: string;
  /**
   * The title/label of this option that is displayed to the customer. The value might change
   * based on region or language. Required by both Android and iOS clients.
   */
  value?: string;
  /** An optional description of this option that is displayed below the `value`. */
  subtitle?: string;
  /** The default case. */
  selectable?: FormBlockerElementOptionPickerElementOptionSelectable | undefined;
  unselectable?:
    | FormBlockerElementOptionPickerElementOptionUnselectable
    | undefined;
  /**
   * Color of subtitle. If nil, subtitle is displayed in default color.
   * This has no effect when Arcade is enabled.
   * Overrides color set based on selectability property. Available in iOS 3.61
   */
  subtitle_color?: FormBlockerElementOptionPickerElementOptionTextColor;
  /**
   * A human-readable string that is displayed to the customer.
   * It is positioned below the `subtitle`.
   * Optional.
   */
  eyebrow_header_text?: string;
  /**
   * Color of eyebrow header. If nil, eyebrow header is displayed in tint color.
   * This has no effect when Arcade is enabled, the blocker tint color is always used.
   * Optional.
   */
  eyebrow_header_color?: Color;
  /** A markdown variant of the `subtitle` string. When set, this takes precedence over `subtitle`. */
  subtitle_markdown?: string;
  /**
   * If you have a better accessibility value for the subtitle label than just its value, put it here.
   * If you are using `subtitle_markdown`, you should populate this field. The markdown formatting characters
   * can upset screen readers, and you may intend to reword emphasis or strikethrough ranges.
   */
  subtitle_accessibility_value?: string;
  /** An image to append to the end of `subtitle` or `subtitle_markdown` with a nonbreaking space character */
  subtitle_trailing_icon?: Icon;
  /**
   * Triggered whenever this option is selected. The on_deselect_actions for the previously selected
   * element will always be executed before the on_select_action of this element. Optional.
   */
  on_select_actions?: BlockerAction[];
  /**
   * Triggered whenever this option is deselected. The on_deselect_actions for the currently selected
   * element will always be executed before the on_select_actions. Optional.
   */
  on_deselect_actions?: BlockerAction[];
}

export enum FormBlockerElementOptionPickerElementOptionTextColor {
  DEFAULT = "DEFAULT",
  /** WARNING - Red */
  WARNING = "WARNING",
}

export interface FormBlockerElementOptionPickerElementOptionConfirmationDialog {
  /** A required short title summarizing the action being confirmed, such as “Confirm Deletion”. This should be in title case. */
  title?: string;
  /**
   * A required message with details about the action being confirmed, such as “This will permanently delete Oscar’s phone number.
   * This should use complete sentences and be in sentence (normal) case.
   */
  message?: string;
  /**
   * The text of button used to confirm the action. This should be as descriptive of the action as possible, such as “Delete”.
   * If nothing is sufficiently short then use “OK”. This field is required.
   */
  confirm_button_text?: string;
  /**
   * The text of button used to cancel performing the action. This will usually be something like “Cancel” or “Later”.
   * This field is not required.
   */
  cancel_button_text?: string;
}

export interface FormBlockerElementOptionPickerElementOptionSelectable {
  /** An optional confirmation dialog to present to the user when this option is selected. */
  confirmation_dialog?: FormBlockerElementOptionPickerElementOptionConfirmationDialog;
}

export interface FormBlockerElementOptionPickerElementOptionUnselectable {
  /**
   * A message to be presented to the customer when they attempt to select an unselectable
   * option. This field is expected to be non-null.
   */
  message?: string;
}

export interface FormBlockerElementOptionPickerElementStyle {
  /** Deprecated and not implemented on the clients. Radio button orientation. If nil, defaults to LEFT. */
  orientation?: FormBlockerElementOptionPickerElementStyleOrientation;
  /** Deprecated and not implemented on the clients. If nil, defaults to DEAFULT. */
  selectionStyle?: FormBlockerElementOptionPickerElementStyleSelectionStyle;
}

/** Radio button orientation in relation to value and subtitle. */
export enum FormBlockerElementOptionPickerElementStyleOrientation {
  LEFT = "LEFT",
  RIGHT = "RIGHT",
}

/** Deprecated and not implemented on the clients. The style to apply to the selected Option. */
export enum FormBlockerElementOptionPickerElementStyleSelectionStyle {
  /** DEFAULT - No border on selection and no background color. */
  DEFAULT = "DEFAULT",
  /** BORDERED - Add a border to the Option when selected and a background color when not selected. */
  BORDERED = "BORDERED",
}

/**
 * An informational text row which has information at both the left and right sides.
 * This element is ideal for displaying key/value paired information, and multiple detail
 * row elements are often stacked adjacent to each other.
 *
 * Optionally, a subtitle may be added as well, which will be displayed on the left side
 * below the title. An image may also be added and will be displayed to the left
 * of the title/subtitle if used.
 */
export interface FormBlockerElementDetailRowElement {
  /**
   * Text shown at the left of the row. Generally the key in a key/value pair. Can support
   * Markdown.
   */
  title?: string;
  /** Text shown at the right of the row. Generally the value in a key/value pair. Can Markdown. */
  detail_text?: string;
  /** Text shown at the left of the row, below the title. Can support Markdown. */
  subtitle?: string;
  /**
   * Action taken when the left side of the row is tapped.
   * Clients will automatically add an indicator that the left side is tappable.
   */
  title_action?: BlockerAction;
}

/**
 * An informational row with a title, subtitle, optional detail text beneath the subtitle,
 * and optional icon at the left side. May or may not have an action. If the action is set, the
 * row is tappable and we display a chevron icon on the right edge of the row. If the action is
 * empty, we don't show any chevron and the row isn't tappable.
 */
export interface FormBlockerElementSelectableRowElement {
  /** The title of the option. Displayed as bold text. */
  title?: string;
  /** Additional text to describe the option. Shown below the title. */
  subtitle?: string;
  /** Styling for `subtitle`. Optional; defaults to `DEFAULT`. */
  subtitle_text_style?: FormBlockerElementSelectableRowElementDetailTextStyle;
  /**
   * Used for call-to-action or an important detail (ex. fee). May be null.
   * Displayed in an accent color, unless detail_text_style is set.
   */
  detail_text?: string;
  /** Accessibility label for this option. */
  accessibility_text?: string;
  /**
   * Action taken when anywhere in the row is tapped. May be null if the row is not tappable.
   * DEPRECATED in favor of row_action below.
   *
   * @deprecated
   */
  action?: BlockerAction;
  /**
   * Local icon at the left side of the row. May be null.
   * DEPRECATED in favor of local_icon below. If a local_icon is provided, icon should still
   * be set as a fallback in order to support older clients that do not support local_icon.
   * The hierarchy of the icon is as follows:
   *   avatar > remote_image> local_icon > icon
   */
  icon?: FormBlockerElementSelectableRowElementIcon;
  /** Used for client side styling of the cell. */
  appearance?: FormBlockerElementSelectableRowElementAppearance;
  /** Action taken when anywhere in the row is tapped. May be null if the row is not tappable. */
  row_action?: FormBlockerElementSelectableRowElementAction;
  /**
   * Remote image displayed at the left side of the row. May be null.
   * Will replace a local icon if both are provided, once downloaded.
   * The hierarchy of the icon is as follows:
   *   avatar > remote_image> local_icon > icon
   * Available in client versions >= 3.53.
   */
  remote_image?: Image;
  /**
   * Styling for the detail_text. May be null.
   * Available in client versions >= 3.87.
   */
  detail_text_style?: FormBlockerElementSelectableRowElementDetailTextStyle;
  /**
   * Display the SelectableRowElement with a border stroke and an
   * inset. This gives the row the appearance of a button instead of
   * a table row. See
   * https://www.figma.com/file/9ElWLOdF0HOQjZj3Bf9ddF/%F0%9F%90%BD-Savings?type=design&node-id=6859-165376&mode=design&t=w4xWIcEitzmsUXhv-0
   * for an example.
   * This has no effect when Arcade is enabled.
   */
  inset_and_outlined?: boolean;
  /**
   * Local Ui icon at the left side of the row. May be null.
   * The hierarchy of the icon is as follows:
   *   avatar > remote_image> local_icon > icon
   * Available for Android version >= 4.49. For iOS version >= 4.48.
   */
  local_icon?: Icon;
  /**
   * The avatar to be displayed. Optional.
   * The avatar will be displayed to the left of the title.
   * The hierarchy of the icon is as follows:
   *   avatar > remote_image> local_icon > icon
   * Available for Android version >= ?.??. For iOS version >= ?.??.
   */
  avatar?: UiAvatar;
  /**
   * Icon directly trailing the title. May be null.
   * Available for Android version >= 5.6.0. For iOS version >= 5.4.0.
   */
  title_icon?: FormBlockerElementSelectableRowElementTitleIcon;
}

/** Usage in `SelectableRowElement.icon` is DEPRECATED in favor of `SelectableRowElement.local_icon`. */
export enum FormBlockerElementSelectableRowElementIcon {
  KEYPAD = "KEYPAD",
  CARD = "CARD",
  UP_AND_DOWN_ARROWS = "UP_AND_DOWN_ARROWS",
  LOCK = "LOCK",
  TRUCK = "TRUCK",
  UNCHECKED_BOX = "UNCHECKED_BOX",
  CHECKED_BOX = "CHECKED_BOX",
  LIGHTNING = "LIGHTNING",
  CALENDAR = "CALENDAR",
  INBOX = "INBOX",
  /** CHECKMARK - Available on client versions >= v3.27 */
  CHECKMARK = "CHECKMARK",
  EXCLAMATION_MARK = "EXCLAMATION_MARK",
  CLOCK = "CLOCK",
  DOLLAR = "DOLLAR",
  INFORMATION = "INFORMATION",
  NUMBER_ONE = "NUMBER_ONE",
  NUMBER_TWO = "NUMBER_TWO",
  NUMBER_THREE = "NUMBER_THREE",
  NUMBER_FOUR = "NUMBER_FOUR",
  NUMBER_FIVE = "NUMBER_FIVE",
  NUMBER_SIX = "NUMBER_SIX",
  NUMBER_SEVEN = "NUMBER_SEVEN",
  NUMBER_EIGHT = "NUMBER_EIGHT",
  NUMBER_NINE = "NUMBER_NINE",
  /** BANK - Available on client versions >= v3.38 */
  BANK = "BANK",
  SHIELD_CHECKMARK = "SHIELD_CHECKMARK",
  DOCUMENT = "DOCUMENT",
  PERSON = "PERSON",
  ENCIRCLED_PLUS = "ENCIRCLED_PLUS",
  /** GLOBE - Available on client versions >= v3.53 */
  GLOBE = "GLOBE",
  CONTACTLESS = "CONTACTLESS",
  /** LOCATION - Available on client version >= v3.54 */
  LOCATION = "LOCATION",
  MOBILE_DEVICE = "MOBILE_DEVICE",
  /** GIFT_CARD - Available in 3.60.+ */
  GIFT_CARD = "GIFT_CARD",
  /** PRICE_TAG - Available in 3.60.+ */
  PRICE_TAG = "PRICE_TAG",
  /** HEART - Available in 3.63.+ */
  HEART = "HEART",
  /** BULLET - Available in 3.65.+ */
  BULLET = "BULLET",
  FDIC_INSURED = "FDIC_INSURED",
  /** FAMILY - Available in 3.70.+ */
  FAMILY = "FAMILY",
  PRIVACY = "PRIVACY",
  UPPER_LIMIT = "UPPER_LIMIT",
  CASH_APP = "CASH_APP",
  /** INVESTING - Available in 3.71.+ */
  INVESTING = "INVESTING",
  CAPITOL = "CAPITOL",
  /** ZERO_CROSSED - Available in 3.74+ */
  ZERO_CROSSED = "ZERO_CROSSED",
  /** HAPPY_FACE - Available in 3.75+ */
  HAPPY_FACE = "HAPPY_FACE",
  /** SPEND_LOCATION - Available in 3.79+ */
  SPEND_LOCATION = "SPEND_LOCATION",
  CARD_BALANCE = "CARD_BALANCE",
  TAP_PAYMENT = "TAP_PAYMENT",
  NOTE = "NOTE",
  NEVER_EXPIRES = "NEVER_EXPIRES",
  /** DOCUMENTS_OUTLINE - Available in 3.81+ */
  DOCUMENTS_OUTLINE = "DOCUMENTS_OUTLINE",
  DOCUMENTS_FINANCIAL = "DOCUMENTS_FINANCIAL",
  DOCUMENTS_W2 = "DOCUMENTS_W2",
  DOCUMENTS_PAYSTUB = "DOCUMENTS_PAYSTUB",
  DOCUMENTS_WILL = "DOCUMENTS_WILL",
  DOCUMENTS_COURT_ORDER = "DOCUMENTS_COURT_ORDER",
  DOCUMENTS_CERTIFICATE = "DOCUMENTS_CERTIFICATE",
  /** GOVERNMENT_IDENTIFIER - Available in 3.85+ */
  GOVERNMENT_IDENTIFIER = "GOVERNMENT_IDENTIFIER",
  /** RECURRING_DOUBLE_ARROW - Available in 3.92+ */
  RECURRING_DOUBLE_ARROW = "RECURRING_DOUBLE_ARROW",
  /** FAVORITES - Available in 3.99+ */
  FAVORITES = "FAVORITES",
  CONTACTS = "CONTACTS",
  /**
   * PROHIBITED - TODO(nowusu) add the min version when it is known
   * Available in ?.??+
   */
  PROHIBITED = "PROHIBITED",
  /** SAVINGS_GROWTH - Available in 4.9+ */
  SAVINGS_GROWTH = "SAVINGS_GROWTH",
  DIRECT_DEPOSIT = "DIRECT_DEPOSIT",
  ROUND_UP = "ROUND_UP",
  /** EDIT - Available in 4.10+ */
  EDIT = "EDIT",
  /** BUSINESS - Available in 4.12+ */
  BUSINESS = "BUSINESS",
  /** SQUARE - Available in 4.38+ */
  SQUARE = "SQUARE",
  /** PERCENT - Available on iOS in 4.38+, Android in 4.39+ */
  PERCENT = "PERCENT",
  /** LOCK_UNLOCKED - Available on iOS in 4.38+, Android in 4.39+ */
  LOCK_UNLOCKED = "LOCK_UNLOCKED",
  /** CALENDAR_PAY_LATER - Available on iOS in 4.38+, Android in 4.39+ */
  CALENDAR_PAY_LATER = "CALENDAR_PAY_LATER",
  /** TICKER_UP - Available on iOS in 4.38+, Android in 4.39+ */
  TICKER_UP = "TICKER_UP",
  /** MAGIC - Available on iOS in 4.38+, Android in 4.39+ */
  MAGIC = "MAGIC",
  /** SENSITIVE_VISIBLE - Available on iOS in 4.40+, Android in 4.40+ */
  SENSITIVE_VISIBLE = "SENSITIVE_VISIBLE",
  /** COMM_CHAT - Available on iOS in 4.40+, Android in 4.41+ */
  COMM_CHAT = "COMM_CHAT",
  /** CURRENCY_BTC - Available on iOS in 4.44+, Android in 4.44+ */
  CURRENCY_BTC = "CURRENCY_BTC",
  /** PHONE - Available on iOS in 4.45+, Android in 4.59+ */
  PHONE = "PHONE",
  /** ATM_CARD - Available on iOS in 4.45+, Android in 4.59+ */
  ATM_CARD = "ATM_CARD",
  /** BELL - Not available */
  BELL = "BELL",
  /** CATEGORY_FOOD_DRINK - Not available */
  CATEGORY_FOOD_DRINK = "CATEGORY_FOOD_DRINK",
  CASH_APP_PAY = "CASH_APP_PAY",
  DEPOSIT_PAPER = "DEPOSIT_PAPER",
  /** OVERDRAFT_CHECKMARK - Available on iOS in 4.59+, Android in 4.59+ */
  OVERDRAFT_CHECKMARK = "OVERDRAFT_CHECKMARK",
}

export enum FormBlockerElementSelectableRowElementAppearance {
  /** STANDARD - Icons are the default icon color without tint. */
  STANDARD = "STANDARD",
  /** DISABLED - Icons are tinted to appear disabled. */
  DISABLED = "DISABLED",
  /** TINTED - Icons are tinted to the accent color set in BlockerDescriptor's theme. Available in 3.63.+ */
  TINTED = "TINTED",
}

/** Optional styling for text. */
export enum FormBlockerElementSelectableRowElementDetailTextStyle {
  /** DEFAULT - Text will be displayed in an accent color. */
  DEFAULT = "DEFAULT",
  /** CRITICAL - Text will be displayed in red. */
  CRITICAL = "CRITICAL",
  /** LABEL - Text will be displayed in gray. */
  LABEL = "LABEL",
  /** WARNING - Text will be displayed in orange. */
  WARNING = "WARNING",
}

/** Action taken when anywhere in the row is tapped. */
export interface FormBlockerElementSelectableRowElementAction {
  /**
   * Optional--if populated, the button will be a secondary button with text;
   * if empty, the button will be a chevron.
   */
  label?: string;
  action?: BlockerAction;
}

/** The icon displayed directly trailing the title. */
export interface FormBlockerElementSelectableRowElementTitleIcon {
  icon?: Icon;
  /** Optional color used to tint the icon. If not set default local color is used. */
  tint_color?: Color;
}

/**
 * A specific glyph which the client knows how to render.
 *
 * Icons should generally be something which isn't easily represented by a `RemoteImageElement`
 * and is not downloadable from the server. This usually implies that the icon is animated or
 * is necessary in an offline scenario.
 */
export interface FormBlockerElementLocalImageElement {
  icon?: FormBlockerElementLocalImageElementIcon;
  /**
   * Local Ui icon. May be null.
   * Available for Android version >= 5.4. For iOS version >= 4.90.
   *
   * `icon` must be set for older clients to prevent crashes until the minimum
   * required version includes support.
   */
  local_icon?: Icon;
}

/** Usage in `LocalImageElement.icon` is DEPRECATED in favor of `LocalImageElement.local_icon`. */
export enum FormBlockerElementLocalImageElementIcon {
  /** INSTANT - Show an animated lightning bolt */
  INSTANT = "INSTANT",
  /** SUCCESS - Show an animated checkmark */
  SUCCESS = "SUCCESS",
  /** FAILURE - Show an X icon: (X) */
  FAILURE = "FAILURE",
  /** PENDING - Show an ellipsis icon: (•••) */
  PENDING = "PENDING",
  /** ACTION_REQUIRED - Show an exclamation point icon: ( ! ) */
  ACTION_REQUIRED = "ACTION_REQUIRED",
  /** LOCKED - Show a lock icon */
  LOCKED = "LOCKED",
  /** RECURRING - Show a recurring icon: ( ↻ ) */
  RECURRING = "RECURRING",
  /** BORROW - Show the instant borrow icon */
  BORROW = "BORROW",
  /** DEPOSIT - Show the deposit icon */
  DEPOSIT = "DEPOSIT",
  /** VERIFICATION - Show the verification shield icon with a checkmark. This */
  VERIFICATION = "VERIFICATION",
  /** VERIFYING - Show the verification shield icon with a lock. This */
  VERIFYING = "VERIFYING",
  /** DOCUMENT - Show a document icon */
  DOCUMENT = "DOCUMENT",
  /** SCRIBBLE - Show a scribble icon */
  SCRIBBLE = "SCRIBBLE",
  /** QR_CODE - Show a QR Code icon. */
  QR_CODE = "QR_CODE",
  /** CVV - DEPRECATED; Will not be available as ARCADE icon asset - https://www.notion.so/cashappcash/Local-Image-1f421ae99a5348b68096c0adfd2e7ab3 */
  CVV = "CVV",
  /**
   * CARD_MAILER_HERO - Cash Card Mailer Hero Image. Full width image
   * DEPRECATED; Unavailable on iOS 5.18+
   *
   * @deprecated
   */
  CARD_MAILER_HERO = "CARD_MAILER_HERO",
  /** CARD_CVV_LOCATION_HERO - Cash Card CVV Location Hero Image. Full width image */
  CARD_CVV_LOCATION_HERO = "CARD_CVV_LOCATION_HERO",
  /** CARD - Show a Cash Card Icon */
  CARD = "CARD",
  /** BANK - Show a bank icon (Supported by iOS 3.27+ and Android 3.28+) */
  BANK = "BANK",
  /** SECURITY_WARNING - Show a filled shield icon with "!". Added in 3.30 */
  SECURITY_WARNING = "SECURITY_WARNING",
  /** CALENDAR - Show a calendar icon */
  CALENDAR = "CALENDAR",
  /** CHECK_DEPOSIT_ENDORSEMENT_HERO - Check Deposit Endorsement Hero Image. Full width image */
  CHECK_DEPOSIT_ENDORSEMENT_HERO = "CHECK_DEPOSIT_ENDORSEMENT_HERO",
  /** ENVELOPE - Show an envelope icon */
  ENVELOPE = "ENVELOPE",
  /** LOGOMARK - Show the Cash logo icon */
  LOGOMARK = "LOGOMARK",
  /** CAMERA - Show the Camera icon. Added in 3.46. */
  CAMERA = "CAMERA",
  /** VERIFICATION_FAILED - Show a filled shield icon with a "!" in a circle. Added in 3.60 */
  VERIFICATION_FAILED = "VERIFICATION_FAILED",
  /** INVESTING_ROUND_UPS - Used by Investing Round Ups * */
  INVESTING_ROUND_UPS = "INVESTING_ROUND_UPS",
  /** INVESTING_ROUND_UPS_INTRO_ILLUSTRATION - Used by Investing Round Ups for the illustration on its introduction screen * */
  INVESTING_ROUND_UPS_INTRO_ILLUSTRATION = "INVESTING_ROUND_UPS_INTRO_ILLUSTRATION",
  /** UNLOCK - Show the unlock icon. Added in 3.70 */
  UNLOCK = "UNLOCK",
  /** FAMILY - Show the family icon. Added in 3.70 */
  FAMILY = "FAMILY",
  /** PERSON - Show the person icon. Added in 3.70 */
  PERSON = "PERSON",
  /** INVESTING - Show the investing icon. */
  INVESTING = "INVESTING",
  /** GOAL - Show the goal icon. Used by Cash Banking Money Management */
  GOAL = "GOAL",
  /** WIRE - Show the wire transfer icon. Added in 3.82. */
  WIRE = "WIRE",
  /** GLOBE - Show the globe icon. Used by Remittances */
  GLOBE = "GLOBE",
  /** RECURRING_DOUBLE_ARROW - Show the recurring icon with two arrows. Used for Recurring Payments. */
  RECURRING_DOUBLE_ARROW = "RECURRING_DOUBLE_ARROW",
  /** CARD_TOP_RIGHT_PLUS - Show the cash card top right plus icon []+, Added in 3.92 */
  CARD_TOP_RIGHT_PLUS = "CARD_TOP_RIGHT_PLUS",
  /** CARD_CHECKMARK - Show the cash card with checkmark icon []V, Added in 3.92 */
  CARD_CHECKMARK = "CARD_CHECKMARK",
  /** CARD_EXPIRED - Show the cash card expired icon []x, Added in 3.92 */
  CARD_EXPIRED = "CARD_EXPIRED",
  /** LOCATION - Show the location icon, Added in 3.92 */
  LOCATION = "LOCATION",
  /** WARNING - Show the warning ⚠️ icon, Added in 3.92 */
  WARNING = "WARNING",
  /** DECLINED - Show the declined 🚫 icon, Added in 3.92 */
  DECLINED = "DECLINED",
  /** DIVIDEND_REINVESTMENT_HERO - Show the dividend reinvestment hero image. Added in 3.99 */
  DIVIDEND_REINVESTMENT_HERO = "DIVIDEND_REINVESTMENT_HERO",
  /** BELL - TODO(anuli): Not available yet. Add in min client version once it's known. */
  BELL = "BELL",
  /** ELIGIBILITY_WARNING - Show an exclamation point icon with black bg: ( ! ). Added in 4.26 */
  ELIGIBILITY_WARNING = "ELIGIBILITY_WARNING",
  /** LOCK_IN_SHIELD - A green shield with a white lock icon. Min version is 4.26 */
  LOCK_IN_SHIELD = "LOCK_IN_SHIELD",
  /** DASH - A dash in a green circle ( - ). Min version is 4.26 */
  DASH = "DASH",
  /** GREEN_EXCLAMATION - Show an exclamation point icon with green bg: ( ! ). Min version is 4.43 */
  GREEN_EXCLAMATION = "GREEN_EXCLAMATION",
  /** NOTE - Show a note icon (i.e. pencil, pen) */
  NOTE = "NOTE",
  /** CLEAR - Show a clear icon (i.e. trash, garbage, delete). Min version is 4.73 */
  CLEAR = "CLEAR",
  /** SAVINGS - Show a savings icon. Min version is 4.77 */
  SAVINGS = "SAVINGS",
  /** BITCOIN - Show a bitcoin icon. Min version is 4.77 */
  BITCOIN = "BITCOIN",
}

/**
 * An image displayed at a specified size. The size is defined as screen-scale-independent
 * points (not pixels). Thus, an image with a width of 100 points would be displayed at 200
 * pixels on a 2x screen, and 300 points on a 3x screen. If the width and height are larger
 * than the device screen allows, the image will be proportionally constrained to fit on
 * the screen.
 *
 * If the RemoteImageElement is the first element on a FormBlocker,
 * `BlockerDescriptor.full_bleed_background_color` may be defined to cause the image to
 * extend to the top and side edges of the device, instead of using the standard element
 * padding.
 *
 * The image is downloaded from the given URL and cached at the time that the form blocker
 * is received. The image download should be considered part of the same network
 * transaction as the one which updates the scenario plan. Any errors encountered during
 * the download should be shown to the user as if they were encountered during the server
 * call which included the form blocker in its response.
 */
export interface FormBlockerElementRemoteImageElement {
  /** @deprecated */
  url?: string;
  /** The width at which to display the image in screen points. This field is required. */
  width?: number;
  /** The height at which to display the image in screen points. This field is required. */
  height?: number;
  /** The horizontal alignment of the image. */
  alignment?: FormBlockerElementRemoteImageElementHorizontalAlignment;
  image?: Image;
  /** The optional accessibility label used to describe the information the image is providing. By default will be null. Available in client version 4.32+. */
  accessibility_label?: string;
  /**
   * Optional tint to be applied to the image.
   * If this field is set the image will use a template rendering mode
   * If not set the image will render using the original color
   * Supported by mobile app version >= 5.21.0
   */
  tint_color?: Color;
}

export enum FormBlockerElementRemoteImageElementHorizontalAlignment {
  /** START - In LTR locales this means left aligned. */
  START = "START",
  /** CENTER - Center align the image. */
  CENTER = "CENTER",
  /** END - In LTR locales this means right aligned. */
  END = "END",
  /**
   * FULL_BLEED - Use the full screen width when displaying the image
   * This allows full bleed images without using BlockerDescriptor.full_bleed_background_color
   * Supported by mobile app version >= 5.21.0
   */
  FULL_BLEED = "FULL_BLEED",
}

/**
 * A monetary amount, displayed as text. The currency symbol should be displayed smaller
 * than the numeric value.
 */
export interface FormBlockerElementMoneyElement {
  amount?: Money;
  /** Available for Android version >= X.XX. For iOS version >= 4.89. */
  alignment?: FormBlockerElementMoneyElementHorizontalAlignment;
  /** Available for Android version >= X.XX. For iOS version >= 4.89. */
  format_style?: FormBlockerElementMoneyElementMoneyFormatStyle;
}

export enum FormBlockerElementMoneyElementHorizontalAlignment {
  /** START - In LTR locales this means left aligned. */
  START = "START",
  /** CENTER - Center align the content. */
  CENTER = "CENTER",
  /** END - In LTR locales this means right aligned. */
  END = "END",
}

export enum FormBlockerElementMoneyElementMoneyFormatStyle {
  /** STANDARD - Typical display of dollars and cents, e.g. "$25.00" or "¥1,000" */
  STANDARD = "STANDARD",
  /** COMPACT - Typical display of dollars only, or dollars and cents, e.g. "$25" or "$24.99" */
  COMPACT = "COMPACT",
}

/** Text, such as a title, summary, or footer. */
export interface FormBlockerElementTextElement {
  /** The text to display. This text may contain links specified as Markdown. */
  text?: string;
  localized_text?: LocalizableString;
  /** The size at which to display the text. */
  size?: FormBlockerElementTextElementSize;
  /** The horizontal alignment of the text. */
  alignment?: FormBlockerElementTextElementHorizontalAlignment;
  /**
   * Text color, if nil text will be displayed in default color.
   * Introduced in iOS 3.61
   */
  text_color?: FormBlockerElementTextElementTextColor;
  /**
   * Arcade typography style
   * If set the client will use this instead of size.
   * If the client does not support this typography it will fallback to the text style from `size`
   * Supported by mobile app version >= 5.21.0
   */
  typography?: Typography;
}

export enum FormBlockerElementTextElementSize {
  /** SMALL - Maps to Arcade's Body Small type style */
  SMALL = "SMALL",
  /** MEDIUM - Maps to Arcade's Body Default type style */
  MEDIUM = "MEDIUM",
  /** LARGE - Maps to Arcade's Page Title type style */
  LARGE = "LARGE",
  /** EXTRA_LARGE - Maps to Arcade's Hero type style. Supported on version 4.16+ */
  EXTRA_LARGE = "EXTRA_LARGE",
}

export enum FormBlockerElementTextElementHorizontalAlignment {
  /** START - In LTR locales this means left aligned. */
  START = "START",
  /** CENTER - Center align the content. */
  CENTER = "CENTER",
  /** END - In LTR locales this means right aligned. */
  END = "END",
}

export enum FormBlockerElementTextElementTextColor {
  DEFAULT = "DEFAULT",
  /** WARNING - Red */
  WARNING = "WARNING",
  /** BRAND - Cash Green. Available client version >= 4.9 */
  BRAND = "BRAND",
  /** TERTIARY - Tertiary Label. Available client version >= 4.32 */
  TERTIARY = "TERTIARY",
}

/**
 * A flexible amount of vertical space.
 *
 * A `SpacerElement` expands to fill all of the vertical space remaining on the screen
 * after adding the other content elements.
 *
 * If multiple `SpacerElement`s are specified in the `elements` array then they each
 * consume a portion of the open vertical space as specified by the `proportion` field.
 * For example, if two `SpacerElement`s are specified, each with a proportion of `1`
 * (or any value so long as they're equal), then each spacer will consume half of the
 * available vertical empty space. If the two elements had `proportion`s of `1` and `2`,
 * then the latter spacer would be twice as tall as the former.
 */
export interface FormBlockerElementSpacerElement {
  proportion?: number;
}

/**
 * A fixed amount of vertical space matching the Arcade spacers. The space may include a divider
 * to match the Arcade specification.
 *
 * FormBlocker clients automatically include spacing between elements so this should only be used when
 * explicitly specified in Figma designs.
 *
 * Supported by mobile app version >= 5.17
 */
export interface FormBlockerElementFixedSpacerElement {
  within_section?: WithinSection | undefined;
  between_section?:
    | BetweenSection
    | undefined;
  /**
   * A custom amount of blank space that can be used to override defaults spacing
   * WARNING: This should only be used as a last resort when default spacings do not match designs.
   * Before using this field check with #cash-form-blocker to see if it is needed.
   */
  custom?: number | undefined;
}

/** Displays a Cash Card, including the customization. */
export interface FormBlockerElementCustomizedCardElement {
  card_customization?: CardCustomizationData;
}

/**
 * Represents an element with an animation, title, and subtitle, each of which are optional.
 * The elements should be vertically stacked in the aforementioned order.
 * We may opt to add support for an image in the hero in the future.
 * That would be a good time to add support for an `AnimationType` parameter to
 * allow for disambiguation.
 */
export interface FormBlockerElementHeroElement {
  /** The URL and the size(in points) of the animation. */
  animation?: Animation;
  /** The string to display for the title. Shown underneath the hero animation. */
  title?: string;
  /** The string to display for the subtitle. Shown underneath the title. */
  subtitle?: string;
  /**
   * The percentage of the form's total width to use, discounting form margins.
   * The height should be calculated by:
   * (form width * animation_width_percentage) * animation.aspectRatio
   */
  animation_width_percentage?: number;
}

/**
 * Displays a timeline. A vertical set of events connected by a line with information shown at
 * each event.
 */
export interface FormBlockerElementTimelineElement {
  events?: FormBlockerElementTimelineElementEvent[];
  /**
   * How many events should be shown above the "show X more" line.
   * This collapses events at the end of the list.
   */
  collapsed_size?:
    | number
    | undefined;
  /** A list of event ranges that should be collapsed on the timeline. */
  collapse_ranges?: TimelineCollapseRanges | undefined;
}

export interface FormBlockerElementTimelineElementEvent {
  icon?: FormBlockerElementTimelineElementEventIcon;
  /** Shown immediately beside the icon. Ex: Date. */
  title?: string;
  /**
   * Additional information shown beside the title (right aligned). Ex: Amount.
   * This field is optional.
   */
  detail_text?: string;
  /**
   * Information to further describe the title text. Shown in different format
   *  (Ex: lighter text color) directly after title text.
   */
  inline_description_text?: string;
  /**
   * Used to style inline_description_text. Not optional if [inline_description_text] is
   * present.
   * This has no effect when Arcade is enabled.
   */
  inline_description_text_format?: FormBlockerElementTimelineElementEventInlineTextFormat;
}

export enum FormBlockerElementTimelineElementEventIcon {
  /** NORMAL - The default state. Usually used for an item in the future. */
  NORMAL = "NORMAL",
  /**
   * HIGHLIGHT - Used to draw focus to an event, such as by drawing the ring in an accent color.
   * Ex: Indicating the next loan payment.
   */
  HIGHLIGHT = "HIGHLIGHT",
  /**
   * ALERT - Used to draw attention to an event that requires immediate action, such as by drawing
   * the ring in red. Ex: An overdue loan payment.
   */
  ALERT = "ALERT",
  /** COMPLETED - Indicates that the event was successful, such as by showing a check mark. */
  COMPLETED = "COMPLETED",
  /** SKIPPED - Indicates that the event was skipped, such as by showing '>>'. */
  SKIPPED = "SKIPPED",
  /** MISSED - Indicates that the event was missed, such as by showing an 'x'. */
  MISSED = "MISSED",
  /**
   * HIGHLIGHT_COMPLETED - Used to draw focus to an event that was successful, such as by drawing the ring
   * in an accent color behind a check mark icon.
   * Available on Android clients 3.62+.
   */
  HIGHLIGHT_COMPLETED = "HIGHLIGHT_COMPLETED",
  /** FAILED - Indicates that the event has failed, such as by showing an 'x' with a red background. */
  FAILED = "FAILED",
  /** CANCELED - The event has been canceled. Available on 3.96+. */
  CANCELED = "CANCELED",
  /** REFUNDED - The event represents a refunded payment. Available on 3.96+. */
  REFUNDED = "REFUNDED",
}

export enum FormBlockerElementTimelineElementEventInlineTextFormat {
  /** PRIMARY - Matches title text. */
  PRIMARY = "PRIMARY",
  /** SECONDARY - Used to not draw primary focus away from title. */
  SECONDARY = "SECONDARY",
}

/** Displays text that is suitable for legal disclaimers, opt-out messaging or other legalese. */
export interface FormBlockerElementLegalTextElement {
  /** Markdown legal text to display as part of this element. */
  text?: string;
}

/**
 * Represents an element with title, subtitle, and button, along with optional
 * color for tint of button. This element is shown with a surrounding border
 * to make it clear that it is single cohesive element.
 */
export interface FormBlockerElementCallToActionElement {
  /**
   * Title of the element. May be null if subtitle is not null.
   * Supports Markdown starting 3.72+.
   */
  title?: string;
  /**
   * Subtitle of the element. May be null if title is not null.
   * Supports Markdown starting 3.72+.
   */
  subtitle?: string;
  /** Image to display above the title. May be null. */
  image?: Image;
  /**
   * NOT IMPLEMENTED IN IOS OR ANDROID YET.
   * The width at which to display the image in screen points.
   * If the specified width is less than or equal to the available width based on the screen size, then the image is displayed at the specified width and height, centered in the space.
   * If the specified width is greater than the available width, then the image is scaled down to match the available width, with the height being scaled down proportionally.
   */
  image_width?: number;
  /**
   * NOT IMPLEMENTED IN IOS OR ANDROID YET.
   * The height at which to display the image in screen points.
   * It will be scaled down when the specified width is greater than the available width. See more details at the "image_width" field.
   */
  image_height?: number;
  /**
   * The action for the button displayed at the bottom of the element.
   * May be null in which case no button will be shown.
   */
  button_action?: BlockerAction;
  /**
   * The accent color used to tint the button specified by button_action, if any.
   * If null, the button will use the accent color specified in the blocker descriptor's theme.
   *
   * Not used in Arcade
   */
  accent_color?: Color;
  /** @deprecated */
  product_discovery_data?: FormBlockerElementCallToActionElementProductDiscoveryData;
}

/**
 * DEPRECATED: Do not use.
 *
 * @deprecated
 */
export interface FormBlockerElementCallToActionElementProductDiscoveryData {
  product_discovery_flow_token?: string;
  product_major?: string;
  product_minor?: string;
  product_category?: string;
  placement_name?: string;
  content_format?: string;
  content_template_token?: string;
  content_token?: string;
}

/**
 * An element displaying an investing tile. The tile will be horizontally aligned and will
 * consist of a remote icon and a caption. The tile's background color is also defined.
 * Clients are to add a graph image to the background of the tile, between the icon and the
 * caption.
 * For example: https://www.figma.com/file/1GPhh2imz3ukEGxFmLnI0U/Stock%2FBTC-Gifting?node-id=326%3A41164
 */
export interface FormBlockerElementCaptionedTileElement {
  icon?: Image;
  /** The accent color to use as the background of the element. */
  background_color?: Color;
  caption?: string;
  accessibility_text?: string;
}

/**
 * An element containing a gift card.
 * Encompasses the card itself as well as the title presented above it and the caption
 * presented below it.
 * For example: https://www.figma.com/file/C2qjjbKQ4ojevq9Q64P95K/Gift-Cards?node-id=1186%3A44796
 */
export interface FormBlockerElementGiftCardElement {
  /** The message attached to the gift card by the sender. */
  note?: string;
  /**
   * Deprecated in favor of "card" which has the company logo on a color background as
   * one image.
   */
  card_logo?: Image;
  /** The amount the gift card is for, formatted for display. */
  amount_text?: string;
  /**
   * An informative caption about this card, set by Cash App business logic, not
   * the gift card sender.
   */
  caption_text?: string;
  /** Accessibility label for the Gift Card. */
  accessibility_label?: string;
  /**
   * The company logo on top of a color background as one image. Clients are responsible
   * for rounding image corners if required.
   */
  card?: Image;
}

/** For cases where the card is shown outside of a badged view */
export interface FormBlockerElementCardElement {
  /** The remote image. Clients will round corners of this image. */
  image?: Image;
  /** The width to display in points. */
  width?: number;
  /** The height to display in points. */
  height?: number;
}

/**
 * A group of vertically stacked or a single element that can
 * be copied to a user's clipboard.
 */
export interface FormBlockerElementCopyableElementGroup {
  elements?: FormBlockerElementCopyableElementGroupCopyableElement[];
}

export interface FormBlockerElementCopyableElementGroupCopyableElement {
  /**
   * The value to be copied, e.g. an account number,
   * phone number, etc. without formatting applied.
   * An example is 2155551234 as a phone number.
   */
  value?: string;
  /**
   * The formatted value to display, e.g. a phone number as
   * (215) 555-1234, which is more readable, but potentially
   * less usable when copied. This is optional if no formatting is required.
   */
  formatted_value?: string;
  /** The label, e.g. "Phone number." */
  label?: string;
  /**
   * The accessibility text when selected, e.g.
   * "Click to copy the phone number of 2155551234."
   */
  accessibility_text?: string;
}

/** Intended to highlight a piece of information for the customer */
export interface FormBlockerElementNoticeElement {
  /** The body of the notice */
  text?: string;
  /** The icon displayed alongside the notice */
  icon?: FormBlockerElementNoticeElementIcon;
}

export enum FormBlockerElementNoticeElementIcon {
  PADLOCK = "PADLOCK",
}

/** Element for choosing an emoji from a specified list. */
export interface FormBlockerElementEmojiPickerElement {
  categories?: FormBlockerElementEmojiPickerElementEmojiCategory[];
  initial_selection?: FormBlockerElementEmojiPickerElementInitialSelection;
}

export interface FormBlockerElementEmojiPickerElementEmojiCategory {
  /** @deprecated */
  options?: string[];
  emojiOptions?: FormBlockerElementEmojiPickerElementEmojiCategoryEmojiOption[];
}

/**
 * The options likely each have a uniform unicode scalar prefix,
 * but we don't want to be strictly constrained to this setup.
 * So, we will repeat the full string for each option.
 *
 * We're anticipating a situation like a "family" emoji category
 * in the picker that we would like to provide a wide range for,
 * some of which start with a different scalar. Example:
 *
 * U+1F468 U+200D U+1F468 U+200D U+1F467 :family_man_man_girl:
 * U+1F469 U+200D U+1F469 U+200D U+1F467 :family_woman_woman_girl:
 *
 * In the initial implementation of the emoji picker, we'll have
 * only one option per category.
 */
export interface FormBlockerElementEmojiPickerElementEmojiCategoryEmojiOption {
  /**
   * This field is deprecated. Once its replacement, `icon_id`, is populated it will be marked as such and
   * client support will be removed.
   */
  unicodeEmoji?: string;
  accessibilityText?: string;
  /** An identifier for this Savings/ArcadeEmoji icon. */
  icon_id?: string;
}

/**
 * An index path specifying the initially selected emoji category
 * and variant within
 */
export interface FormBlockerElementEmojiPickerElementInitialSelection {
  categories_index?: number;
  options_index?: number;
}

/**
 * THIS ELEMENT WILL BE DEPRECATED. Please use UpsellElement instead.
 *
 * Intended to display family upsell contents. An example:
 * https://www.figma.com/file/5jucIPYCk9oMuSKgR2ydFV/%F0%9F%91%A8%E2%80%8D%F0%9F%91%A9%E2%80%8D%F0%9F%91%A7%E2%80%8D%F0%9F%91%A6-Families-v.2-Stocks%26Bitcoin?node-id=2430%3A272437
 * Supported in 3.74+ (due to image sizing bugs in 3.73)
 */
export interface FormBlockerElementFamilyUpsellElement {
  /** Title of the element. May be null if subtitle is not null. */
  title?: string;
  /** Subtitle of the element. May be null if title is not null. */
  subtitle?: string;
  /**
   * The width at which to display the image in screen points.
   * If the specified width is less than or equal to the available width based on the screen size, then the image is displayed at the specified width and height, centered in the space.
   * If the specified width is greater than the available width, then the image is scaled down to match the available width, with the height being scaled down proportionally.
   */
  image_width?: number;
  /**
   * The height at which to display the image in screen points.
   * It will be scaled down when the specified width is greater than the available width. See more details at the "image_width" field.
   */
  image_height?: number;
  /** Image to display above the title. May be null. */
  image?: Image;
}

/**
 * Intended to display upsell contents (originally known as FamilyUpsellElement).
 * Supported in Android 3.93+ and iOS 3.92+.
 */
export interface FormBlockerElementUpsellElement {
  /** Title of the element. May be null if subtitle is not null. */
  title?: string;
  /** Subtitle of the element. May be null if title is not null. */
  subtitle?: string;
  /**
   * The width at which to display the image in screen points.
   * If the specified width is less than or equal to the available width based on the screen size, then the image is displayed at the specified width and height, centered in the space.
   * If the specified width is greater than the available width, then the image is scaled down to match the available width, with the height being scaled down proportionally.
   * May be null if image is null.
   */
  image_width?: number;
  /**
   * The height at which to display the image in screen points.
   * It will be scaled down when the specified width is greater than the available width. See more details at the "image_width" field.
   * May be null if image is null.
   */
  image_height?: number;
  /** Image to display above the title. May be null. */
  image?: Image;
  /**
   * The action for the button displayed at the bottom of the element.
   * May be null in which case no button will be shown.
   *
   * Supported in client versions TBD (TODO: kma)
   */
  button_action?: BlockerAction;
  product_discovery_data?: FormBlockerElementUpsellElementProductDiscoveryData;
  on_demand_message_data?: FormBlockerElementUpsellElementOnDemandMessageData;
}

/**
 * For UpsellElements produced from Product Discovery content, this message contains the
 * various data fields needed to publish ProductDiscovery CDP events.
 */
export interface FormBlockerElementUpsellElementProductDiscoveryData {
  product_discovery_flow_token?: string;
  product_major?: string;
  product_minor?: string;
  product_category?: string;
  placement_name?: string;
  content_format?: string;
  content_template_token?: string;
  content_token?: string;
}

/**
 * For UpsellElements produced from Messaging Platform's On Demand API, this message contains the
 * various data fields needed to publish On Demand Message CDF events.
 */
export interface FormBlockerElementUpsellElementOnDemandMessageData {
  campaign_token?: string;
  placement?: string;
  product_type?: string;
  template_token?: string;
  template_version?: number;
  format?: string;
}

export interface FormBlockerElementMerchantTransactionElement {
  /** The unique identifier of the transaction. This will be returned when used in option picker */
  transactionId?: string;
  /** Determine whether or not this selectable and should render selectable layout */
  selectable?: boolean;
  /** The merchant's avatar urls */
  avatar?: Avatar;
  /** Determine whether or not to show the alert badge on the avatar */
  avatarBadged?: boolean;
  /** The formatted amount of money spent at the merchant */
  amount?: FormBlockerElementMerchantTransactionElementAmount;
  /**
   * Pre formatted date information shown verbatim on client
   * E.g. "Sept 20th @ 9:30pm" or "Yesterday"
   */
  date?: string;
  /**
   * The first of optionally 2 lines describing the location of the purchase
   * E.g. "Modesto, CA" or "Online"
   */
  locationLineOne?: string;
  /**
   * The second of 2 lines describing the location of the purchase
   * E.g. "In-person"
   */
  locationLineTwo?: string;
  /**
   * The text used to describe the status of the payment.
   * E.g. "Declined"
   */
  transactionStatus?: string;
  /**
   * The color being applied to the transactionStatus text
   * E.g. red
   */
  transactionStatusColor?: Color;
  /**
   * The name of the merchant that the transaction was with
   * E.g. Starbucks
   */
  merchantName?: string;
  /**
   * When the merchant transaction element is interacted with,
   * it will perform the action passed here.
   */
  actions?: BlockerAction[];
}

export interface FormBlockerElementMerchantTransactionElementAmount {
  /** The amount of Money spent at the merchant */
  money?: Money;
  /**
   * The markdown applied to the amount text.
   * Only provide markdown for one side of the text and client will apply to both sides
   * E.g "~~" for strikethrough text
   */
  markdown?: string;
}

export interface FormBlockerElementMerchantTransactionOptionPicker {
  options?: FormBlockerElementMerchantTransactionElement[];
}

/** Intended to display a check box with a description to the user. */
export interface FormBlockerElementCheckBoxElement {
  /**
   * Description for the check box. Not optional.
   * E.g. "Do you want to check this box or not?"
   */
  text?: string;
  /** The default value to use for the check box. Not optional. */
  default_value?: boolean;
  /**
   * If this field is `true`, the form blocker can not be submitted
   * until the checkbox is checked. The field is `false` by default.
   *
   * See also `fulfills_requirements`, which offers the ability to group these elements where at least 1 must be
   * checked to pass validation.
   */
  is_required?: boolean;
  /**
   * Message to display when the checkbox is required but not checked.
   * Only displayed if is_required is true.
   * E.g. "You must accept the terms to continue"
   * Optional - if not provided, clients should use a default message.
   *
   * ** iOS only - Android disables the button if checkbox is not present. **
   */
  validation_message?: string;
  /**
   * Controls the position and visual styling of the checkbox relative to the text.
   * If not specified, defaults to DEFAULT
   */
  style?: FormBlockerElementCheckBoxElementStyle;
  /**
   * If this field is populated, an Image asset will be rendered at the start of the check box.
   *
   * Supported in clients 4.91+
   */
  image?: Image;
  /**
   * If this field is populated, the avatar will be rendered at the start of the check box.
   * If both `avatar` and `image` are populated, `avatar` takes priority.
   *
   * Supported in clients 4.91+
   */
  avatar?: UiAvatar;
  /**
   * Secondary text that provides additional context below the main description. Optional.
   *
   * Supported in clients 4.92+"
   */
  body_text?: string;
  /**
   * Declares the requirements that selecting this checkbox fulfills. A blocker is considered invalid and cannot
   * be submitted until all of the requirements that appear in checkbox elements have at least 1 checked element.
   *
   * On iOS, that validation takes place upon tapping the primary action button.
   * On Android, the primary action button is disabled until selection has taken place.
   *
   * If this checkbox should not affect the validation/submission of the screen, this field should be empty and
   * `is_required` should not be set to true.
   *
   * If this checkbox, on its own, must be checked to proceed, it can contain a string that does not
   * appear in any other checkbox elements on the blocker. An easier way to accomplish this, though, is by setting
   * the `is_required` field.
   *
   * If this checkbox is part of a _group_ of checkboxes where at least 1 must be checked, each element in the group
   * should contain the same String.
   *
   * On iOS, failing validation shows an error String found in `validation_message`. The error String displayed will
   * come from the `validation_message` of the first checkbox element containing an unfulfilled requirement.
   *
   * `is_required` remains a valid field that can be set to cause a validation failure if this checkbox is
   * not checked.
   *
   * Supported in clients 5.0+
   */
  fulfills_requirements?: string[];
}

export enum FormBlockerElementCheckBoxElementStyle {
  /**
   * DEFAULT - Used for standard checkboxes, such as those in a list.
   *
   * Available in iOS version >= 4.84, Android version >= 4.85.
   */
  DEFAULT = "DEFAULT",
  /**
   * LEGAL_AGREEMENT - Used for legal agreement checkboxes, such as to agree to a terms of service.
   *
   * Available in iOS version >= 4.84, Android version >= 4.85.
   */
  LEGAL_AGREEMENT = "LEGAL_AGREEMENT",
  /** TOGGLE - Used for toggle switches, such as those in a settings screen. */
  TOGGLE = "TOGGLE",
}

/**
 * An outlined box containing a header and detail row elements.
 * https://www.figma.com/file/WD3IBrAOCQrOR6XzTY39Di/%F0%9F%9A%A7---Payment-Plans-(fka-Retro-Financing)?node-id=2285%3A204823&t=qhmn4qzT4Xw1EiwB-4
 */
export interface FormBlockerElementPaymentPlanSummaryElement {
  /** The header, not optional. */
  header?: string;
  /** The summary item rows, minimum 1. */
  items?: FormBlockerElementDetailRowElement[];
}

/** Summarizes an upcoming payment schedule using circular progress items. */
export interface FormBlockerElementPaymentPlanScheduleElement {
  /** The header text. Not optional. */
  header?: string;
  /** Body text to show beneath the header. Optional. */
  body?: string;
  /** Array of progress element objects. May be empty. */
  circle_progress_items?: FormBlockerElementPaymentPlanScheduleElementCircleProgressItem[];
  /**
   * Icon representing the source of the scheduled payments. Optional.
   * This has no effect when Arcade is enabled.
   */
  source_icon?: Image;
  /** Text describing the source of the scheduled payments. Supports markdown. Optional. */
  source_text?: string;
  /** Action to take when the source info section is clicked. */
  source_info_click_action?: BlockerAction;
}

/** A circle with amount progress cleared, a larger title below the circle, and subtitle below. */
export interface FormBlockerElementPaymentPlanScheduleElementCircleProgressItem {
  /**
   * How much to fill in the circles outer ring. Not optional.
   * 0.0 = 0%, 1.0 = 100%
   * This has no effect when Arcade is enabled.
   */
  progress_percent?: number;
  /**
   * The label that appears inside the circle. One character. Optional.
   * This has no effect when Arcade is enabled.
   */
  circle_label?: string;
  /** Bolded text that appears under the circle. Optional. */
  primary_text?: string;
  /** Not bolded text that appears under primary text. Optional. */
  secondary_text?: string;
}

/**
 * A field to input a location without specific address. It will return
 * neighborhood, locality and administrative_area_1 on GlobalAddress.
 */
export interface FormBlockerElementLocationElement {
  /** The initial value with which to populate the address fields. Optional. */
  prefill_address?: GlobalAddress;
  /** The country where the expected address is located. Optional. */
  country_code?: Country;
}

export interface FormBlockerElementVerificationCodeElement {
  /** The expected number of digits to be entered. Should not be null */
  expected_digits_count?: number;
}

/**
 * A vertical list of rows matching the Arcade list-unordered component
 * All labels have limited markdown support
 * https://www.figma.com/design/fu8vNWffYkGUMyznuQyzhc/02-Arcade---Mobile?node-id=1318-14971&p=f&t=jNHRgJWyOzhCEqJX-0
 * Available in iOS and Android from 4.96.0. https://block.atlassian.net/browse/FORMB-6
 */
export interface FormBlockerElementListUnorderedElement {
  /** Array of items to be displayed vertically in the list */
  items?: FormBlockerElementListUnorderedElementItem[];
  /** Display type to be used for rendering the list */
  type?: FormBlockerElementListUnorderedElementType;
  /**
   * Size type of the list. Affects spacing between items
   * Will use COMPACT if not set
   * Supported by mobile app version >= 5.21.0
   */
  size?: ListUnorderedSize;
}

export enum FormBlockerElementListUnorderedElementType {
  /** STANDARD - Standard rendering. Should use 24pt icons. This is the default. */
  STANDARD = "STANDARD",
  /** SUBTLE - Smaller and lighter text. Should use 16pt icons */
  SUBTLE = "SUBTLE",
}

/** A single row in the list */
export interface FormBlockerElementListUnorderedElementItem {
  /** The label in the left column. Required. */
  label?: string;
  /** Body that sits below label. */
  body?: string;
  /** Value shown in the right column. */
  value?: string;
  /** Icon for the row. */
  icon?: Icon;
}

/**
 * A vertical list of rows matching the Arcade list-ordered component
 * https://www.figma.com/design/fu8vNWffYkGUMyznuQyzhc/02-Arcade---Mobile?node-id=73280-128556&t=Gnc0ShWooMnq7Kld-0
 * Available in client versions >= 5.21.
 */
export interface FormBlockerElementListOrderedElement {
  /** Arcade ListOrdered message */
  list?: ListOrdered;
}

/**
 * Header element with a title, body, and show more button.
 * https://www.figma.com/design/fu8vNWffYkGUMyznuQyzhc/02-Arcade---Mobile?node-id=73280-99014
 * https://block.atlassian.net/browse/FORMB-69
 * Available in client versions >= 5.13.
 */
export interface FormBlockerElementSectionHeaderElement {
  /** Required title. */
  title?: string;
  /** Optional body. Use to provide more context about the section. Supports markdown */
  body?: string;
  /**
   * Action to take when show more is clicked.
   * The text from the BlockerAction will be used for the title
   */
  show_more_action?: BlockerAction;
}

/**
 * Standard cell with multiple possible configurations.
 * https://www.figma.com/design/fu8vNWffYkGUMyznuQyzhc/02-Arcade---Mobile?node-id=73280-112199&t=zmV4LFY6gu3XKWNC-0
 * https://block.atlassian.net/browse/FORMB-79
 * Available in client versions >= 5.11.
 */
export interface FormBlockerElementCellDefaultElement {
  /** Arcade CellDefault message */
  cell?: CellDefault;
  /** Action to take when the cell is pressed */
  cell_action?: BlockerAction;
  /**
   * When set, cells with the same id should operate as a single group for selection.
   * So selecting one radio button from the group will reset the others.
   * If not set then each cell will act independently
   *
   * When a value is set for the selection group,
   * at least 1 selection must be made in the group in order for the FormBlocker to be valid
   */
  selection_group_id?: string;
  /**
   * If true the cell will start in the selected state.
   * For cells not using checkbox or radio this will have no effect
   */
  initial_selection?: boolean;
}

/**
 * Header that serves as the overview or title element on NUX screens and expressive moments.
 * https://www.figma.com/design/fu8vNWffYkGUMyznuQyzhc/02-Arcade---Mobile?node-id=73280-98976&t=uS97NQvF7GOcVojC-0
 * Available in client versions >= 5.22.
 */
export interface FormBlockerElementHeroHeaderElement {
  /** Arcade HeroHeader message */
  header?: HeroHeader;
}

/**
 * Header used for balances.
 * https://www.figma.com/design/fu8vNWffYkGUMyznuQyzhc/02-Arcade---Mobile?node-id=75739-1786
 * TODO: Add client support https://block.atlassian.net/browse/FORMB-230
 */
export interface FormBlockerElementHeroNumericsHeaderElement {
  /** Arcade HeroNumericsHeader message */
  header?: HeroNumericsHeader;
}

/**
 * Represents an icon that can be either a predefined image
 * or a reference to a custom Arcade icon.
 * This icon is only used in Form Blocker CTA buttons and is optional elsewhere.
 */
export interface FormBlockerIcon {
  /** A predefined image type (e.g., Apple Wallet, Google Wallet). */
  image?:
    | FormBlockerIconImage
    | undefined;
  /** Reference to a custom Arcade icon asset. */
  arcade_icon?: Icon | undefined;
}

/** Represents predefined image types that can be displayed in buttons. */
export interface FormBlockerIconImage {
  type?: FormBlockerIconImageType;
}

export enum FormBlockerIconImageType {
  TYPE_UNSPECIFIED = "TYPE_UNSPECIFIED",
  MOBILE_WALLET = "MOBILE_WALLET",
}

/** A card element with a hero image, title and details in a column */
export interface FormBlockerEditorialContentElement {
  /** Title of the element shown below the image. */
  title_text?: string;
  /** Body text of the element. */
  detail_text?: string;
  /** Image to display above the title. */
  hero_image?: Image;
}

export interface FormBlockerSubformsEntry {
  key: string;
  value?: FormBlocker;
}

export interface FormBlockerRemoteOnDisplayEffect {
  animation?: Animation;
}
